import { format, isSameDay } from "date-fns";
import React from "react";

import { faCalendarDays } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Icon, Text } from "@performyard/py-components";

import TruncatedText from "../../../../../components/Text/TruncatedText";
import { GoogleCalendarIcon } from "../../../../UserSettings/Views/Calendar/components/GoogleCalendarIcon";
import { OutlookCalendarIcon } from "../../../../UserSettings/Views/Calendar/components/OutlookCalendarIcon";
import { useMeetingContext } from "./MeetingContext";
import MeetingStatusBadge from "./MeetingStatusBadge";

const getFormattedMeetingDateString = (startDateTime: Date, endDateTime: Date) => {
	const startDate = format(startDateTime, "MMMM d, yyyy");
	const endDate = format(endDateTime, "MMMM d, yyyy");
	const startTime = format(startDateTime, "h:mmaaa");
	const endTime = format(endDateTime, "h:mmaaa");

	if (!isSameDay(startDateTime, endDateTime)) {
		return `${startDate} • ${startTime} - ${endDate} • ${endTime}`;
	} else {
		return `${startDate} • ${startTime} - ${endTime}`;
	}
};

const MeetingModalHeader = () => {
	const { meeting } = useMeetingContext();

	// Determine calendar integration display
	const renderCalendarIcon = () => {
		// Show generic calendar icon if meeting is not connected to calendar
		if (!meeting.calendarEvent?.eventId) {
			return <Icon size={20} icon={faCalendarDays} />;
		}

		// Show provider-specific icon based on the provider that was used to create the calendar event
		// This prevents showing the wrong icon when users switch providers after creating events
		const calendarProvider = meeting.calendarEvent?.provider;
		if (calendarProvider === "google") {
			return (
				<div className="flex h-5 w-5 items-center justify-center">
					<GoogleCalendarIcon />
				</div>
			);
		} else if (calendarProvider === "outlook") {
			return (
				<div className="flex h-5 w-5 items-center justify-center">
					<OutlookCalendarIcon />
				</div>
			);
		}

		// Fallback to generic calendar icon if provider is not recognized
		return <Icon size={20} icon={faCalendarDays} />;
	};

	return (
		<div
			className="flex h-14 select-none items-center justify-between rounded-lg bg-neutral-1 px-4"
			data-testid="meeting-modal-header"
		>
			<div className="flex items-center space-x-4">
				{renderCalendarIcon()}
				<div className="flex flex-col items-start">
					<TruncatedText
						text={meeting.name}
						maxWidth={1000}
						maxLines={1}
						tooltipPlacement="top"
						useTooltip="on-truncate"
						size="paragraph-2"
						weight="semi-bold"
					/>
					<Text size="caption" weight="regular">
						{getFormattedMeetingDateString(meeting.startDateTime, meeting.endDateTime)}
					</Text>
				</div>
			</div>
			<div className="flex items-center">
				<MeetingStatusBadge status={meeting.status} />
			</div>
		</div>
	);
};

export default MeetingModalHeader;
